import { Fragment, ReactNode, useState } from "react";
import { isReasoningUIPart, isToolUIPart } from "ai";
import { Button } from "react-aria-components";
import cx from "classnames";

import { ChevronDown } from "components/icons/generated";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import MarkdownAssistantPanel from "components/markdown/MarkdownAssistantPanel";

import { AiChatThinkingPart } from "../Messages/helpers";
import { ToolPart } from "../Tool/helpers";
import { getHeaderLabel } from "./helpers";
import styles from "./styles.module.css";

type AiChatThinkingProps = {
  parts: AiChatThinkingPart[];
  isLive: boolean;
  renderTool: (part: ToolPart) => ReactNode;
};

const AiChatThinking = ({ parts, isLive, renderTool }: AiChatThinkingProps) => {
  const hasPendingApproval = parts.some((part) => part.state === "approval-requested");
  const [openedBy, setOpenedBy] = useState<"user" | "approval" | undefined>(
    hasPendingApproval ? "approval" : undefined
  );
  const [previousHasPendingApproval, setPreviousHasPendingApproval] = useState(hasPendingApproval);

  if (hasPendingApproval !== previousHasPendingApproval) {
    setPreviousHasPendingApproval(hasPendingApproval);

    if (hasPendingApproval && !openedBy) {
      setOpenedBy("approval");
    }

    if (!hasPendingApproval && openedBy === "approval") {
      setOpenedBy(undefined);
    }
  }

  const isOpen = !!openedBy;

  const lastTool = parts.findLast(isToolUIPart);
  const hasThought = parts.some((part) => isReasoningUIPart(part) && part.text.trim());

  if (!lastTool && !hasThought && !isLive) {
    return null;
  }

  return (
    <Box className={styles.thinking} direction="column" align="start" fullWidth>
      <Box align="center" gap="medium" fullWidth>
        <Button
          type="button"
          aria-expanded={isOpen}
          onPress={() => setOpenedBy(isOpen ? undefined : "user")}
          className={styles.header}
        >
          <Box align="center" gap="small">
            <Typography tag="span" variant="p-body3" className={styles.label}>
              {getHeaderLabel(lastTool, isLive)}
              {isLive && "..."}
            </Typography>
            <Icon
              src={ChevronDown}
              noShrink
              className={isOpen ? styles.chevronOpen : styles.chevron}
            />
          </Box>
        </Button>
        {hasPendingApproval && !isOpen && <Badge type="weak" variant="yellow" text="Approval" />}
      </Box>

      <Box className={cx(styles.content, isOpen && styles.contentOpen)} fullWidth inert={!isOpen}>
        <Box direction="column" className={styles.contentInner}>
          <Box direction="column" gap="x-large" padding="medium 0">
            {parts.map((part, index) => {
              if (!isReasoningUIPart(part)) {
                return <Fragment key={index}>{renderTool(part)}</Fragment>;
              }

              if (!part.text.trim()) {
                return null;
              }

              return (
                <Box key={index} direction="column" className={styles.thought}>
                  <MarkdownAssistantPanel>{part.text}</MarkdownAssistantPanel>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AiChatThinking;
